<template>
  <div>
    <!-- Search bar with filters - styled like AccountList -->
    <div class="px-2 mt-3 mb-0">
      <v-text-field
        v-model="search"
        placeholder="Zoeken in contacten..."
        solo
        clearable
        @click:clear="clearSearch"
        @input="onSearchInput"
      >
        <template #append>
          <CounterBadgeIcon
            :counter="activeFilterCount"
            icon="mdi-account-filter"
            @click="openFilterWindow"
          />
          <v-btn
            icon
            variant="text"
            title="Ververs contacten lijst"
            class="mr-2"
            @click="syncWithStore"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            title="Nieuw contact toevoegen"
            @click="createNewContact"
          >
            <v-icon start>mdi-plus</v-icon>
            Nieuw contact
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Quick filter shortcuts -->
    <div class="px-2 mb-3">
      <div class="d-flex align-center justify-space-between mb-2">
        <v-chip-group
          v-model="selectedTypes"
          multiple
          selected-class="text-primary"
          class="flex-grow-1"
        >
          <v-chip
            filter
            variant="outlined"
            value="organization"
            :prepend-icon="contactsStore.icons.organization.icon"
            :color="
              selectedTypes.includes('organization')
                ? contactsStore.icons.organization.color
                : undefined
            "
            @click="quickFilterByType('organization')"
          >
            <span class="font-weight-medium">
              Bedrijven
              <v-chip
                size="x-small"
                variant="tonal"
                :color="contactsStore.icons.organization.color"
                class="ml-1"
              >
                {{ contactCounts.organization }}
              </v-chip>
            </span>
          </v-chip>

          <v-chip
            filter
            variant="outlined"
            value="department"
            :prepend-icon="contactsStore.icons.department.icon"
            :color="
              selectedTypes.includes('department')
                ? contactsStore.icons.department.color
                : undefined
            "
            @click="quickFilterByType('department')"
          >
            <span class="font-weight-medium">
              Afdelingen
              <v-chip
                size="x-small"
                variant="tonal"
                :color="contactsStore.icons.department.color"
                class="ml-1"
              >
                {{ contactCounts.department }}
              </v-chip>
            </span>
          </v-chip>

          <v-chip
            filter
            variant="outlined"
            value="individual"
            :prepend-icon="contactsStore.icons.individual.icon"
            :color="
              selectedTypes.includes('individual')
                ? contactsStore.icons.individual.color
                : undefined
            "
            @click="quickFilterByType('individual')"
          >
            <span class="font-weight-medium">
              Personen
              <v-chip
                size="x-small"
                variant="tonal"
                :color="contactsStore.icons.individual.color"
                class="ml-1"
              >
                {{ contactCounts.individual }}
              </v-chip>
            </span>
          </v-chip>
        </v-chip-group>

        <!-- Clear filters button - only show when filters are active -->
        <v-btn
          v-if="hasActiveFilters"
          variant="text"
          size="small"
          color="error"
          @click="clearAllFiltersAndSearch"
        >
          <v-icon start size="small">mdi-filter-remove</v-icon>
          Wis filters
        </v-btn>
      </div>
    </div>

    <!-- Loading spinner -->
    <div
      v-if="isLoading && matchedCount === 0"
      class="text-center py-8"
    >
      <v-progress-circular indeterminate color="primary" size="48" width="4" />
      <p class="text-body-1 text-medium-emphasis mt-4 mb-0">
        Contacten worden geladen...
      </p>
    </div>

    <!-- Main content area -->
    <div v-else>
      <!-- Filter Dialog -->
      <ContactFilterDialog
        v-model:model-value="filterParams"
        :show="showFilterDialog"
        @update:show="showFilterDialog = $event"
        @apply-filters="applyFilters"
        @clear-filters="clearAllFilters"
      />

      <!-- No results message -->
      <div
        v-if="showNoResultsMessage"
        class="d-flex justify-center align-center pa-8"
      >
        <v-card
          class="text-center pa-8 ma-4"
          variant="outlined"
          color="surface"
          max-width="500"
          elevation="0"
        >
          <div class="no-results-container">
            <div class="icon-wrapper mb-6">
              <v-avatar size="80" color="grey-lighten-4" class="mb-4">
                <v-icon size="40" color="grey-lighten-1">
                  mdi-account-search-outline
                </v-icon>
              </v-avatar>
            </div>

            <div class="content-wrapper">
              <h3 class="text-h5 text-grey-darken-2 mb-3 font-weight-medium">
                Geen contacten gevonden
              </h3>
              <p
                class="text-body-1 text-grey-darken-1 mb-6 mx-auto"
                style="max-width: 350px; line-height: 1.5"
              >
                Er zijn geen contacten gevonden die voldoen aan je zoekcriteria.
                Probeer je zoekopdracht aan te passen of wis de filters.
              </p>
            </div>

            <div class="actions-wrapper">
              <v-btn
                variant="elevated"
                color="primary"
                size="large"
                class="px-8 mr-3"
                @click="clearSearch"
              >
                <v-icon start>mdi-filter-remove</v-icon>
                Filters wissen
              </v-btn>

              <v-btn
                variant="outlined"
                color="primary"
                size="large"
                class="px-8"
                @click="createNewContact"
              >
                <v-icon start>mdi-plus</v-icon>
                Nieuw contact
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>

      <!-- Contacts hierarchy -->
      <div v-else class="contact-tree pa-0">
        <!-- Expand / collapse all -->
        <div class="d-flex align-center justify-space-between px-2 py-1">
          <span class="text-caption text-medium-emphasis">
            {{ matchedCount }} contact{{ matchedCount !== 1 ? "en" : "" }}
            {{ isFiltering ? "gevonden" : "" }}
          </span>
          <v-btn
            variant="text"
            size="small"
            :prepend-icon="allExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal'"
            @click="toggleExpandAll"
          >
            {{ allExpanded ? "Alles inklappen" : "Alles uitklappen" }}
          </v-btn>
        </div>

        <ContactTreeNode
          v-for="node in contactTree"
          :key="`root-${node.contact.id}`"
          :node="node"
          :depth="0"
          :expand-directive="expandDirective"
        />

        <!-- All contacts loaded message -->
        <div
          v-if="matchedCount > 0"
          class="d-flex justify-center align-center py-8"
        >
          <v-card
            class="text-center pa-6"
            variant="flat"
            color="grey-lighten-5"
            max-width="400"
            rounded="lg"
          >
            <div class="no-more-container">
              <div class="icon-wrapper mb-4">
                <v-avatar size="56" color="grey-lighten-3" class="mb-2">
                  <v-icon size="28" color="grey-darken-1">
                    mdi-check-circle-outline
                  </v-icon>
                </v-avatar>
              </div>

              <div class="content-wrapper">
                <h4 class="text-h6 text-grey-darken-2 mb-2 font-weight-medium">
                  Alle contacten geladen
                </h4>
                <p
                  class="text-body-2 text-grey-darken-1 mb-0"
                  style="line-height: 1.4"
                >
                  {{ matchedCount }} contact{{ matchedCount !== 1 ? "en" : "" }}
                  gevonden.
                </p>
              </div>
            </div>
          </v-card>
        </div>
      </div>
    </div>

    <!-- Floating action button -->
    <v-fab
      location="bottom end"
      color="primary"
      icon="mdi-plus"
      app
      class="mb-16"
      title="Nieuw contact toevoegen"
      @click="createNewContact"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useContactsStoreRefactor } from "@/stores/contactsStoreRefactor";
import { useMenuStore } from "@/stores/menuStore";
import { useContextMenuStore } from "@/stores/contextMenuStore";
import { useFormsStore } from "@/stores/formsStore";
import ContactTreeNode from "./ContactTreeNode.vue";
import ContactFilterDialog from "./ContactFilterDialog.vue";
import CounterBadgeIcon from "../ui/CounterBadgeIcon.vue";

// Constants
const DEBOUNCE_DELAY = 700;

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Store instances
const contactsStore = useContactsStoreRefactor();
const menuStore = useMenuStore();
const contextMenuStore = useContextMenuStore();
const formsStore = useFormsStore();

// Reactive data
const search = ref("");
const selectedTypes = ref([]);
const isLoading = ref(false);
const showFilterDialog = ref(false);
const filterParams = ref({
  hasEmail: null,
  hasPhone: null,
  hasAddress: null,
  categoryIds: [],
});

// Manual expand/collapse-all override: null = follow default behaviour,
// true/false = force all nodes open/closed.
const expandAllOverride = ref(null);

// Computed properties
const contactTree = computed(() => contactsStore.contactTree);
const matchedCount = computed(() => contactsStore.matchedContactsCount);
const isFiltering = computed(() => contactsStore.isFiltering);
const contactCounts = computed(() => contactsStore.contactCounts);
const activeFilterCount = computed(() => contactsStore.activeFilterCount);

// Tri-state directive handed to the tree:
//   null  -> browse mode, nodes self-manage (top level open, rest collapsed)
//   true  -> force everything open (filtering active, or "expand all")
//   false -> force everything collapsed ("collapse all")
const expandDirective = computed(() => {
  if (expandAllOverride.value !== null) return expandAllOverride.value;
  if (isFiltering.value) return true;
  return null;
});

// For the toggle button: treat browse mode (null) as "expanded-ish".
const allExpanded = computed(() => expandDirective.value !== false);

// When the search/filter state flips, drop any manual expand/collapse override
// so filtering re-drives expansion (otherwise a prior "collapse all" would hide
// freshly matched results).
watch(isFiltering, () => {
  expandAllOverride.value = null;
});

const isSearchActive = computed(() => {
  return search.value.trim() !== "" || activeFilterCount.value > 0;
});

const showNoResultsMessage = computed(() => {
  return isSearchActive.value && matchedCount.value === 0 && !isLoading.value;
});

// Check if any filters are currently active
const hasActiveFilters = computed(() => {
  return (
    search.value.trim() !== "" ||
    selectedTypes.value.length > 0 ||
    activeFilterCount.value > 0
  );
});

// Debounced search
const debouncedSearch = debounce(() => {
  contactsStore.setSearchTerm(search.value);
}, DEBOUNCE_DELAY);

// Methods
const onSearchInput = () => {
  debouncedSearch();
};

const clearSearch = () => {
  search.value = "";
  contactsStore.setSearchTerm("");
};

const clearAllFiltersAndSearch = () => {
  search.value = "";
  selectedTypes.value = [];
  contactsStore.clearFilters();
  filterParams.value = {
    hasEmail: null,
    hasPhone: null,
    hasAddress: null,
    categoryIds: [],
  };
};

const syncWithStore = async () => {
  try {
    isLoading.value = true;
    await contactsStore.getAll();
  } catch (error) {
    console.error("Error syncing contacts:", error);
  } finally {
    isLoading.value = false;
  }
};

const quickFilterByType = (type) => {
  // Toggle type in selection
  const index = selectedTypes.value.indexOf(type);
  if (index > -1) {
    selectedTypes.value.splice(index, 1);
  } else {
    selectedTypes.value.push(type);
  }

  contactsStore.setTypeFilters(selectedTypes.value);
};

const toggleExpandAll = () => {
  // Collapse if currently expanded, otherwise expand everything.
  expandAllOverride.value = allExpanded.value ? false : true;
};

const openFilterWindow = () => {
  showFilterDialog.value = true;
};

const applyFilters = (filters) => {
  filterParams.value = { ...filters };
  contactsStore.setContactDetailFilters(filters);
  showFilterDialog.value = false;
};

const clearAllFilters = () => {
  clearAllFiltersAndSearch();
  showFilterDialog.value = false;
};

const createNewContact = () => {
  // Pass default contact data like ReportForm does instead of empty object
  formsStore.edit({
    type: "contact",
    item: {
      id: null,
      name: "",
      type: "organization",
      parent_id: null,
    },
  });
};

const loadContacts = async () => {
  try {
    isLoading.value = true;
    await contactsStore.getAll();
  } catch (error) {
    console.error("Error loading contacts:", error);
  } finally {
    isLoading.value = false;
  }
};

// Context menu options
const contextMenuOptions = [
  {
    name: "Contact toevoegen",
    action: createNewContact,
    icon: "mdi-plus",
  },
  {
    name: "Lijst verversen",
    action: syncWithStore,
    icon: "mdi-refresh",
  },
];

// Lifecycle
onMounted(async () => {
  menuStore.setMenuVisible(false);
  contextMenuStore.addOption(contextMenuOptions);
  await loadContacts();
});

onUnmounted(() => {
  contextMenuStore.reset();
});
</script>

<style scoped>
.no-results-container,
.no-more-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.icon-wrapper {
  flex-shrink: 0;
}

.content-wrapper {
  flex-grow: 1;
  text-align: center;
}

.actions-wrapper {
  flex-shrink: 0;
}

.v-chip-group {
  justify-content: flex-start;
}

.gap-2 > * + * {
  margin-left: 8px;
}

.filter-summary {
  background-color: rgba(var(--v-theme-surface-variant), 0.5);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.filter-summary:hover {
  background-color: rgba(var(--v-theme-surface-variant), 0.7);
}

/* Ensure filter chips have proper spacing and interaction */
.v-chip--filter {
  transition: all 0.2s ease;
}

.v-chip--filter:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
